import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useAuth } from "../../app/AuthContext";
import { api } from "../../shared/api/client";
import type { ReleaseTemplateSummary } from "../../shared/types/api";
import {
  Button,
  DataTable,
  PageHeader,
  StatusBadge,
  type Column,
} from "../../shared/ui";
import { TemplateFormDrawer } from "./TemplateFormDrawer";
import { TemplateVersionDrawer } from "./TemplateVersionDrawer";
import { RELEASE_TYPE_LABELS } from "../releases/releaseVocabulary";

/**
 * §56's template management screen.
 *
 * <p>Lists what each template would impose and which version is in use, because that is the
 * question an administrator opens this page with. Editing happens a level down, in the version
 * drawer, since a template's name is almost never the thing being changed — its contents are.
 */
export function ReleaseTemplatesPage() {
  const { can } = useAuth();
  const [creating, setCreating] = useState(false);
  const [opened, setOpened] = useState<ReleaseTemplateSummary | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [renaming, setRenaming] = useState<ReleaseTemplateSummary | null>(null);
  const queryClient = useQueryClient();

  /*
   * §46: withdrawing a template stops it being offered for new releases. It deliberately does
   * not touch the releases already built from it — their governance was copied in at creation,
   * which is the whole reason template versions are immutable.
   */
  const setOffered = async (template: ReleaseTemplateSummary, offered: boolean) => {
    setBusy(template.id);
    try {
      await api.post(
        `/api/v1/release-templates/${template.id}/${offered ? "reactivate" : "deactivate"}`,
      );
      void queryClient.invalidateQueries({ queryKey: ["release-templates"] });
    } finally {
      setBusy(null);
    }
  };

  const templates = useQuery({
    queryKey: ["release-templates", includeInactive],
    queryFn: () =>
      api.get<ReleaseTemplateSummary[]>(
        `/api/v1/release-templates?includeInactive=${includeInactive}`,
      ),
  });

  const columns: Column<ReleaseTemplateSummary>[] = [
    {
      key: "name",
      header: "Template",
      render: (template) => (
        <div className="min-w-0">
          <p className="truncate font-medium text-ink-900">{template.name}</p>
          <p className="font-mono text-xs text-ink-400">{template.code}</p>
        </div>
      ),
    },
    {
      key: "applicableReleaseType",
      header: "Applies to",
      render: (template) =>
        template.applicableReleaseType ? (
          <StatusBadge tone="gray">
            {RELEASE_TYPE_LABELS[template.applicableReleaseType]}
          </StatusBadge>
        ) : (
          <span className="text-ink-500">Any release</span>
        ),
    },
    {
      key: "environment",
      header: "Environment",
      render: (template) =>
        template.environment?.name ?? <span className="text-ink-400">—</span>,
    },
    {
      key: "activeVersion",
      header: "In use",
      render: (template) =>
        template.activeVersionNumber == null ? (
          /* A template nobody activated cannot be used to create anything, which is worth
             flagging here rather than at the point somebody tries. */
          <StatusBadge tone="amber">No active version</StatusBadge>
        ) : (
          <span className="tnum text-ink-900">V{template.activeVersionNumber}</span>
        ),
    },
    {
      key: "versionCount",
      header: "Versions",
      render: (template) => <span className="tnum">{template.versionCount}</span>,
    },
    {
      key: "active",
      header: "Status",
      render: (template) => (
        <StatusBadge tone={template.active ? "green" : "gray"}>
          {template.active ? "Offered" : "Withdrawn"}
        </StatusBadge>
      ),
    },
    {
      key: "offered",
      header: "",
      render: (template) =>
        can("RELEASE_TEMPLATE_MANAGE") ? (
          <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            onClick={(event) => {
              event.stopPropagation();
              setRenaming(template);
            }}
          >
            Edit
          </Button>
          <Button
            variant="ghost"
            size="sm"
            loading={busy === template.id}
            onClick={(event) => {
              event.stopPropagation();
              void setOffered(template, !template.active);
            }}
          >
            {template.active ? "Withdraw" : "Offer again"}
          </Button>
          </div>
        ) : null,
    },
    {
      key: "lastModifiedAt",
      header: "Changed",
      render: (template) => (
        <span className="tnum text-xs">
          {new Date(template.lastModifiedAt).toLocaleDateString(undefined, {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })}
        </span>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Release templates"
        subtitle="What a release of each kind is held to. Versioned, so changing one never rewrites the releases already built from it."
        actions={
          can("RELEASE_TEMPLATE_MANAGE") && (
            <Button icon={<Plus className="h-4 w-4" />} onClick={() => setCreating(true)}>
              New template
            </Button>
          )
        }
      />

      <div className="mb-3">
        <label className="flex items-center gap-2 text-sm text-ink-700">
          <input
            type="checkbox"
            checked={includeInactive}
            onChange={(event) => setIncludeInactive(event.target.checked)}
          />
          Include withdrawn templates
        </label>
      </div>

      <DataTable
        columns={columns}
        rows={templates.data ?? []}
        rowKey={(template) => template.id}
        loading={templates.isPending}
        onRowClick={setOpened}
        emptyTitle="No release templates yet"
        emptyMessage="A template is the governance a kind of release is held to — its prerequisites, approvals, runbook, rollback plan and post-deployment checks. Releases copy it, so changing a template never affects one already made."
        emptyAction={
          can("RELEASE_TEMPLATE_MANAGE") ? (
            <Button onClick={() => setCreating(true)}>Create the first template</Button>
          ) : undefined
        }
      />

      <TemplateFormDrawer
        template={renaming}
        open={creating || renaming != null}
        onClose={() => {
          setCreating(false);
          setRenaming(null);
        }}
        onSaved={(created) => created && setOpened(null)}
      />
      <TemplateVersionDrawer
        template={opened}
        onClose={() => setOpened(null)}
      />
    </>
  );
}
